/**
 * Setting your own password.
 *
 * Two jobs, one screen. On a first sign-in it is the *only* screen: the
 * password was chosen by whoever created the account, so until it is replaced
 * the backend refuses every other route and showing a navigation full of
 * things that would 403 is worse than showing none. The rest of the time it is
 * reached from the sidebar and behaves like any other form.
 *
 * The distinction is `forced`. Nothing about the request changes — the same
 * route, the same rules — only what the screen says and whether there is a way
 * out of it.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Me } from '../api'
import { useAuth } from '../auth'
import { Note } from '../components/ui'

/** Matches `passwords.MIN_LENGTH` on the backend, which is the real check. */
const MIN_LENGTH = 12

export default function ChangePassword({
  forced = false,
  onDone,
}: {
  forced?: boolean
  onDone?: () => void
}) {
  const { me, logout } = useAuth()
  const queryClient = useQueryClient()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [done, setDone] = useState(false)

  const change = useMutation({
    mutationFn: () =>
      api.post<Me>('/auth/password', { current_password: current, new_password: next }),
    onSuccess: (updated) => {
      // The session cookie was reissued at a new epoch, so the cached `me` is
      // stale in the one field the whole lockout turns on.
      queryClient.setQueryData(['me'], updated)
      setCurrent('')
      setNext('')
      setAgain('')
      setDone(true)
      onDone?.()
    },
  })

  // Checked here only to say so before the request. The backend refuses both
  // of these too, and its refusal is the one that counts.
  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const mismatch = again.length > 0 && next !== again
  const unchanged = next.length > 0 && next === current
  const ready = current && next.length >= MIN_LENGTH && next === again && !unchanged

  const body = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (ready) change.mutate()
      }}
    >
      {forced && (
        <Note kind="warn">
          The password you signed in with was chosen by whoever set up your
          account, so they know it. Replace it and the rest of the system opens.
        </Note>
      )}
      {change.error && <Note kind="error">{(change.error as Error).message}</Note>}
      {done && !forced && <Note kind="ok">Your password has been changed.</Note>}

      <label>
        <span className="caption">Current password</span>
        <input
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
        />
      </label>
      <label>
        <span className="caption">New password — at least {MIN_LENGTH} characters</span>
        <input
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
        />
      </label>
      <label>
        <span className="caption">New password again</span>
        <input
          type="password"
          autoComplete="new-password"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
          required
        />
      </label>

      {tooShort && <Note kind="warn">That is shorter than {MIN_LENGTH} characters.</Note>}
      {mismatch && <Note kind="warn">The two new passwords do not match.</Note>}
      {unchanged && (
        <Note kind="warn">
          The new password has to be different from the one you signed in with.
        </Note>
      )}

      <button className="primary" type="submit" disabled={!ready || change.isPending}>
        {change.isPending ? 'Saving…' : 'Set my password'}
      </button>
      {forced && (
        <>
          {' '}
          {/* The only other way out. Somebody handed the wrong credentials
              needs to be able to leave rather than be trapped on one screen. */}
          <button type="button" onClick={() => void logout()}>
            Sign out
          </button>
        </>
      )}
    </form>
  )

  if (!forced) {
    return (
      <section className="card" style={{ maxWidth: 460 }}>
        <header>
          <h2>Your password</h2>
          <span className="hint">Nobody can change it for you</span>
        </header>
        <div className="body">{body}</div>
      </section>
    )
  }

  return (
    <div className="login-page">
      <section className="card login-card">
        <div className="body">
          <h1>Set your password</h1>
          <p className="lede">
            Signed in as {me?.email}. This is the only thing this account can do
            until it has a password of its own.
          </p>
          {body}
        </div>
      </section>
    </div>
  )
}
